import type { ComponentProps, ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useFieldControl } from './field'

export const controlBase =
  'w-full min-w-0 rounded-sm border border-rule-strong bg-control text-ink shadow-(--control-shadow) transition-colors duration-100 ' +
  'hover:border-ink-faint focus-visible:border-stamp focus-visible:outline-1 focus-visible:outline-offset-0 ' +
  'aria-invalid:border-ink-red aria-invalid:focus-visible:outline-ink-red ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-faint'

export interface InputProps extends Omit<ComponentProps<'input'>, 'size'> {
  size?: 'md' | 'sm'
  /** Icon or text inside the start edge (e.g. a search glass). */
  leading?: ReactNode
  /** Content inside the end edge (e.g. a clear button or a shortcut hint). */
  trailing?: ReactNode
}

export function Input({ className, size = 'md', leading, trailing, ...props }: InputProps) {
  const field = useFieldControl(props)
  const input = (
    <input
      {...props}
      {...field}
      className={cn(
        controlBase,
        size === 'md' ? 'h-(--control-h) px-2.5 text-base' : 'h-7 px-2 text-sm',
        'read-only:bg-surface-sunken',
        leading ? 'ps-8' : null,
        trailing ? 'pe-9' : null,
        !leading && !trailing ? className : null,
      )}
    />
  )
  if (!leading && !trailing) return input
  // The adornments sit in the input's own direction: a dir="ltr" input (password, e-mail, money) on an RTL
  // page pads its LTR end, so the wrapper must place the trailing icon there too.
  return (
    <div dir={props.dir} className={cn('relative flex items-center', className)}>
      {leading ? (
        <span className="pointer-events-none absolute start-2.5 flex text-ink-muted [&_svg]:size-4">
          {leading}
        </span>
      ) : null}
      {input}
      {trailing ? <span className="absolute end-1.5 flex items-center">{trailing}</span> : null}
    </div>
  )
}

export interface TextareaProps extends ComponentProps<'textarea'> {}

export function Textarea({ className, rows = 3, ...props }: TextareaProps) {
  const field = useFieldControl(props)
  return (
    <textarea
      rows={rows}
      {...props}
      {...field}
      className={cn(controlBase, 'min-h-18 resize-y px-2.5 py-1.5 text-base', className)}
    />
  )
}
